import {
  detectionMediaPath,
  resolveServiceworkerMediaPath,
} from "./alertDetectionMedia";

/** True if path is a canonical `/detections/{id}/photo|video|thumbnail` route. */
export function isDetectionsMediaPath(p) {
  if (typeof p !== "string" || !p.trim()) return false;
  const s = p.trim();
  return /^\/?detections\/[^/]+\/(photo|video|thumbnail)(\/|$|\?)/i.test(s);
}

/**
 * Plate/face crop is served at GET `/detections/{id}/thumbnail` for some use cases only.
 * Object Detection rows often have `/photo` but not a separate thumbnail asset — requesting
 * `/thumbnail` causes 404 noise in Network.
 */
function alertPayloadSupportsDetectionCropThumbnail(
  apiAlert,
  eventJson,
  firstNestedDetection,
) {
  const parts = [
    apiAlert?.use_case_type,
    apiAlert?.use_case_name,
    apiAlert?.alert_type_name,
    apiAlert?.alert_type,
    apiAlert?.message,
    eventJson?.use_case_type,
    eventJson?.use_case_name,
    eventJson?.alert_type,
    eventJson?.alert_type_name,
    firstNestedDetection?.label,
    firstNestedDetection?.class_name,
    firstNestedDetection?.type,
  ];
  const combined = parts
    .filter((x) => x != null && String(x).trim() !== "")
    .map((x) => String(x).toLowerCase().replace(/_/g, " "))
    .join(" ");
  if (!combined.trim()) return false;

  const isAnpr =
    /\banpr\b/.test(combined) ||
    combined.includes("automatic number plate") ||
    combined.includes("number plate recognition") ||
    combined.includes("plate recognition");

  const isFaceRecognition =
    combined.includes("face recognition") ||
    combined.includes("face recognization") ||
    combined.includes("facial recognition");

  return Boolean(isAnpr || isFaceRecognition);
}

/**
 * Resolve photo, thumbnail, crop, and video URLs from an alert/history/stream payload.
 * When `detections.id` or `detection_id` exists, canonical `/detections/{id}/…` paths use the AI detection base.
 *
 * @param {Record<string, unknown> | null | undefined} apiAlert
 * @returns {{
 *   detectionId: string | null;
 *   photoUrl: string;
 *   thumbnailUrl: string;
 *   thumbnailCropUrl: string;
 *   videoUrl: string;
 *   hasVideo: boolean;
 *   hasVideoFromApi: boolean;
 * }}
 */
export function getMediaFieldsFromAlertApiPayload(apiAlert) {
  const empty = {
    detectionId: null,
    photoUrl: "",
    thumbnailUrl: "",
    thumbnailCropUrl: "",
    videoUrl: "",
    hasVideo: false,
    hasVideoFromApi: false,
  };
  if (!apiAlert || typeof apiAlert !== "object") return empty;

  const eventJson =
    apiAlert.event_json && typeof apiAlert.event_json === "object"
      ? apiAlert.event_json
      : {};
  const detBlock = apiAlert.detections;

  /** First detection row when API nests `detections` as an array or under `event_json`. */
  const firstNestedDetection = (() => {
    if (
      Array.isArray(eventJson.detections) &&
      eventJson.detections.length > 0 &&
      typeof eventJson.detections[0] === "object"
    ) {
      return eventJson.detections[0];
    }
    if (
      detBlock &&
      typeof detBlock === "object" &&
      !Array.isArray(detBlock) &&
      Array.isArray(detBlock.detections) &&
      detBlock.detections.length > 0 &&
      typeof detBlock.detections[0] === "object"
    ) {
      return detBlock.detections[0];
    }
    if (
      Array.isArray(detBlock) &&
      detBlock.length > 0 &&
      typeof detBlock[0] === "object"
    ) {
      return detBlock[0];
    }
    return null;
  })();

  const detectionId = (() => {
    if (apiAlert.detection_id != null) {
      const id = String(apiAlert.detection_id).trim();
      if (id) return id;
    }
    if (
      detBlock &&
      typeof detBlock === "object" &&
      !Array.isArray(detBlock) &&
      detBlock.id != null
    ) {
      const id = String(detBlock.id).trim();
      if (id) return id;
    }
    if (firstNestedDetection && firstNestedDetection.id != null) {
      const id = String(firstNestedDetection.id).trim();
      if (id) return id;
    }
    return null;
  })();

  const photoFromApi =
    typeof apiAlert.photo_url === "string" ? apiAlert.photo_url.trim() : "";
  const thumbFromApi =
    typeof apiAlert.thumbnail_url === "string"
      ? apiAlert.thumbnail_url.trim()
      : "";
  const detThumbFromApi = (() => {
    if (
      detBlock &&
      typeof detBlock === "object" &&
      !Array.isArray(detBlock) &&
      typeof detBlock.thumbnail_url === "string"
    ) {
      return detBlock.thumbnail_url.trim();
    }
    if (
      firstNestedDetection &&
      typeof firstNestedDetection.thumbnail_url === "string"
    ) {
      return firstNestedDetection.thumbnail_url.trim();
    }
    return "";
  })();
  const videoFromApi =
    typeof apiAlert.video_url === "string" ? apiAlert.video_url.trim() : "";

  const legacyImageRaw =
    (typeof eventJson.image_url === "string" && eventJson.image_url.trim()) ||
    (typeof apiAlert.thumbnailUrl === "string" && apiAlert.thumbnailUrl.trim()) ||
    (typeof apiAlert.image_url === "string" && apiAlert.image_url.trim()) ||
    (typeof apiAlert.image === "string" && apiAlert.image.trim()) ||
    (typeof apiAlert.thumbnail === "string" && apiAlert.thumbnail.trim()) ||
    "";

  const normalizeRel = (p) => {
    if (typeof p !== "string" || !p.trim()) return "";
    const t = p.trim();
    return t.startsWith("/") ? t : `/${t}`;
  };

  let photoRel = "";
  let thumbRel = "";
  let videoRel = "";

  const videoFromApiNonEmpty =
    Boolean(videoFromApi) &&
    String(videoFromApi).trim() !== "" &&
    String(videoFromApi).toLowerCase() !== "null";
  const apiSaysHasVideoClip =
    videoFromApiNonEmpty ||
    apiAlert.has_video === true ||
    apiAlert.hasVideo === true ||
    apiAlert.media_type === "video";

  if (detectionId) {
    photoRel = detectionMediaPath(detectionId, "photo");
    thumbRel = detectionMediaPath(detectionId, "thumbnail");
    videoRel = detectionMediaPath(detectionId, "video");
  } else {
    photoRel = photoFromApi || legacyImageRaw;
    const explicitThumb = thumbFromApi || detThumbFromApi;
    thumbRel =
      explicitThumb && isDetectionsMediaPath(explicitThumb)
        ? normalizeRel(explicitThumb)
        : explicitThumb || "";
    videoRel =
      videoFromApi && isDetectionsMediaPath(videoFromApi)
        ? normalizeRel(videoFromApi)
        : videoFromApi || "";
  }

  let photoUrl = resolveServiceworkerMediaPath(photoRel) || "";
  const thumbnailOnlyUrl = thumbRel
    ? resolveServiceworkerMediaPath(thumbRel)
    : "";
  const legacyResolved = legacyImageRaw
    ? resolveServiceworkerMediaPath(legacyImageRaw)
    : "";
  let thumbnailUrl =
    thumbnailOnlyUrl || photoUrl || legacyResolved;
  /**
   * History often returns `photo_url` as a storage path (e.g. `tmp/...`) while
   * `detections.id` points at canonical `/detections/{id}/photo`. Prefer canonical,
   * but if resolution left no URL, fall back so list/media filters and thumbnails work.
   */
  if (!String(photoUrl || "").trim() && photoFromApi) {
    photoUrl = resolveServiceworkerMediaPath(normalizeRel(photoFromApi)) || "";
  }
  if (!String(thumbnailUrl || "").trim() && photoUrl) {
    thumbnailUrl = photoUrl;
  }
  const thumbPathKey = normalizeRel(thumbRel || "");
  const photoPathKey = normalizeRel(photoRel || "");
  const thumbAndPhotoAreDistinct =
    Boolean(thumbPathKey) &&
    Boolean(photoPathKey) &&
    thumbPathKey !== photoPathKey;

  const explicitThumbFromApi = Boolean(
    (thumbFromApi && String(thumbFromApi).trim()) ||
      (detThumbFromApi && String(detThumbFromApi).trim()),
  );
  const cropThumbSupported = alertPayloadSupportsDetectionCropThumbnail(
    apiAlert,
    eventJson,
    firstNestedDetection,
  );

  /**
   * Bottom-right crop in alert detail: GET `/detections/{id}/thumbnail` (plate/face crop).
   * Do not assume this asset exists for every detection id (e.g. Object Detection).
   */
  const thumbnailCropUrl =
    (thumbnailOnlyUrl &&
      ((detectionId && (cropThumbSupported || explicitThumbFromApi)) ||
        (!detectionId && thumbAndPhotoAreDistinct))) ?
      thumbnailOnlyUrl
    : "";

  const videoUrl =
    videoRel && String(videoRel).trim()
      ? resolveServiceworkerMediaPath(videoRel) || ""
      : "";

  const videoUrlPresent =
    Boolean(videoUrl) &&
    String(videoUrl).trim() !== "" &&
    String(videoUrl).toLowerCase() !== "null";

  /**
   * `hasVideoFromApi`: payload / explicit URL says a clip exists (PHOTO tab = still-only).
   * `hasVideo`: also true when only `detection_id` + canonical `/video` path (VIDEO tab / player).
   */
  const hasVideoFromApi = Boolean(
    apiSaysHasVideoClip ||
      (videoUrlPresent && (!detectionId || videoFromApiNonEmpty)),
  );
  const hasVideo =
    hasVideoFromApi || (Boolean(detectionId) && videoUrlPresent);

  const videoUrlOut = hasVideo ? videoUrl : "";

  return {
    detectionId,
    photoUrl: photoUrl || "",
    thumbnailUrl: thumbnailUrl || "",
    thumbnailCropUrl: thumbnailCropUrl || "",
    videoUrl: videoUrlOut,
    hasVideo,
    hasVideoFromApi,
  };
}

/**
 * Carousel order: photo → video → thumbnail crop (when distinct).
 * @param {Record<string, unknown> | null | undefined} alert
 * @returns {{ kind: "image" | "video"; url: string }[]}
 */
export function buildAlertHistoryCarouselSlides(alert) {
  if (!alert || typeof alert !== "object") return [];
  const slides = [];
  const detId =
    alert.detectionId != null ? String(alert.detectionId).trim() : "";

  let photo = String(alert.photoUrl || "").trim();
  if (!photo && detId) {
    const rel = detectionMediaPath(detId, "photo");
    if (rel) photo = resolveServiceworkerMediaPath(rel) || "";
  }
  const thumb = String(alert.thumbnailCropUrl || "").trim();

  let video = String(alert.videoUrl || "").trim();
  if (!video && detId && alert?.hasVideo) {
    const rel = detectionMediaPath(detId, "video");
    if (rel) video = resolveServiceworkerMediaPath(rel) || "";
  }

  if (photo) slides.push({ kind: "image", url: photo });
  if (video) slides.push({ kind: "video", url: video });
  if (thumb) slides.push({ kind: "image", url: thumb });
  return slides;
}
